import { parseHost } from '@/Domain/helper/scrapeUrlClassifier';

// Conservative client-side DNS existence check for the onboarding URL.
//
// A browser has no DNS API, but public resolvers expose DNS-over-HTTPS as a
// CORS-enabled JSON endpoint, so we can ask Cloudflare whether a hostname
// resolves before firing the (slow, background) scrape job. This converts the
// most common onboarding failure — a typo'd or non-existent domain that today
// only fails 60-150s later in a background task — into instant inline feedback.
//
// Deliberately conservative: we only report `nxdomain` on a DEFINITIVE
// "this name does not exist" answer (DNS RCODE 3). A resolver outage, timeout,
// rate-limit, non-2xx, or any ambiguous response returns `unknown`, which the
// caller treats as "allow through". A NOERROR with no A records also resolves
// (the domain may serve only AAAA/MX/etc.). This keeps us from ever blocking a
// real or freshly-registered domain whose DNS has not yet propagated.

const DOH_ENDPOINT = 'https://cloudflare-dns.com/dns-query';
const DOH_TIMEOUT_MS = 2500;
const DNS_RCODE_NXDOMAIN = 3;

export type DomainResolveResult = 'resolves' | 'nxdomain' | 'unknown';

interface DohResponse {
  Status?: number;
}

export async function checkDomainResolves(rawUrl: string): Promise<DomainResolveResult> {
  const host = parseHost(rawUrl);
  if (!host) return 'unknown';

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), DOH_TIMEOUT_MS);
  try {
    const response = await fetch(`${DOH_ENDPOINT}?name=${encodeURIComponent(host)}&type=A`, {
      headers: { accept: 'application/dns-json' },
      signal: controller.signal,
    });
    if (!response.ok) return 'unknown';
    const data: DohResponse = await response.json();
    return data.Status === DNS_RCODE_NXDOMAIN ? 'nxdomain' : 'resolves';
  } catch {
    // Abort/timeout, network failure, or unparseable body — never block on uncertainty.
    return 'unknown';
  } finally {
    clearTimeout(timer);
  }
}
